import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Inbox, Check, X, AlertCircle } from 'lucide-react';
import { api } from '../lib/api';
import { Card } from '../components/Card';
import { PageSpinner } from '../components/Spinner';

export function CompAdminApplications() {
  const [params] = useSearchParams();
  const compId = params.get('compId') ?? '';
  const qc = useQueryClient();

  const apps = useQuery({
    queryKey: ['comp-admin-applications', compId],
    queryFn: () => api.listCompAdminApplications(compId),
    enabled: !!compId,
  });

  const decideMut = useMutation({
    mutationFn: ({ applicantId, decision }: { applicantId: string; decision: 'ACCEPTED' | 'REJECTED' }) =>
      api.decideCompAdminApplication(compId, applicantId, decision),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['comp-admin-applications', compId] }),
  });

  if (!compId) {
    return (
      <div className="mx-auto max-w-lg px-4 py-12">
        <Card className="border-amber-700/40">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-6 w-6 shrink-0 text-amber-400" />
            <p className="text-sm text-iron-200">
              Missing <code className="font-mono">?compId=</code> in URL. Open this page from a Discord notification link.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  if (apps.isLoading) return <PageSpinner />;

  if (apps.isError) {
    return (
      <div className="mx-auto max-w-lg px-4 py-12">
        <Card className="border-red-700/40">
          <p className="text-sm text-red-300">{(apps.error as Error).message}</p>
        </Card>
      </div>
    );
  }

  const pending = (apps.data?.applications ?? []).filter(a => a.status === 'PENDING');
  const decided = (apps.data?.applications ?? []).filter(a => a.status !== 'PENDING');

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <div className="mb-6 flex items-center gap-3">
        <Inbox className="h-7 w-7 text-violet-400" />
        <div>
          <h1 className="text-2xl font-bold text-white">Comp-Admin Applications</h1>
          <p className="font-mono text-xs text-gray-500">{compId.replace(/^COMP#/, '')}</p>
        </div>
      </div>

      {pending.length === 0 && decided.length === 0 && (
        <Card>
          <p className="text-sm text-iron-300">No applications yet for this comp.</p>
        </Card>
      )}

      {pending.length > 0 && (
        <>
          <h2 className="mb-2 font-display text-[10px] font-black uppercase tracking-[0.25em] text-forge-500/80">
            Pending — {pending.length}
          </h2>
          <div className="mb-6 space-y-2">
            {pending.map(a => (
              <Card key={a.applicantDiscordId}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-mono text-xs text-iron-300">
                      <span className="text-iron-500">Discord ID:</span> {a.applicantDiscordId}
                    </p>
                    <p className="mt-0.5 text-[10px] text-iron-500">
                      Submitted {new Date(a.createdAt).toLocaleString('en-GB')}
                    </p>
                    {a.message && (
                      <p className="mt-2 rounded border border-forge-900/40 bg-iron-950 px-3 py-2 font-mono text-xs italic text-iron-200">
                        &ldquo;{a.message}&rdquo;
                      </p>
                    )}
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <button
                      onClick={() => decideMut.mutate({ applicantId: a.applicantDiscordId, decision: 'ACCEPTED' })}
                      disabled={decideMut.isPending}
                      className="flex items-center gap-1 border border-emerald-700/50 bg-emerald-900/20 px-3 py-1.5 font-display text-[10px] font-black uppercase tracking-widest text-emerald-300 hover:bg-emerald-900/40 disabled:opacity-50"
                    >
                      <Check className="h-3 w-3" /> Approve
                    </button>
                    <button
                      onClick={() => decideMut.mutate({ applicantId: a.applicantDiscordId, decision: 'REJECTED' })}
                      disabled={decideMut.isPending}
                      className="flex items-center gap-1 border border-red-700/50 bg-red-900/20 px-3 py-1.5 font-display text-[10px] font-black uppercase tracking-widest text-red-300 hover:bg-red-900/40 disabled:opacity-50"
                    >
                      <X className="h-3 w-3" /> Decline
                    </button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </>
      )}

      {decided.length > 0 && (
        <>
          <h2 className="mb-2 font-display text-[10px] font-black uppercase tracking-[0.25em] text-forge-500/80">
            Decided — {decided.length}
          </h2>
          <div className="space-y-2">
            {decided.map(a => (
              <Card key={a.applicantDiscordId} className="opacity-70">
                <div className="flex items-center justify-between gap-3">
                  <p className="font-mono text-xs text-iron-300">{a.applicantDiscordId}</p>
                  <span className={`font-display text-[10px] font-black uppercase tracking-widest ${
                    a.status === 'ACCEPTED' ? 'text-emerald-300' : 'text-red-300'
                  }`}>
                    {a.status}
                  </span>
                </div>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
